import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Linkedin } from "lucide-react";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysFor } from "@/config/cmsSections";

const COL_PRO = [
  { to: "/professionnels", label: "Professionnels" },
  { to: "/professionnels/optimisation-des-contrats", label: "Optimisation des contrats" },
  { to: "/professionnels/anticipation-des-contrats", label: "Anticipation des contrats" },
  { to: "/professionnels/gestion-multi-sites", label: "Gestion multi-sites" },
];
const COL_PART = [
  { to: "/particuliers", label: "Particuliers" },
  { to: "/particuliers/comparer-les-offres", label: "Comparer les offres" },
  { to: "/particuliers/changer-de-fournisseur", label: "Changer de fournisseur" },
  { to: "/particuliers/demenagement", label: "Déménagement" },
  { to: "/particuliers/comprendre-sa-facture", label: "Comprendre sa facture" },
];
const COL_SITE = [
  { to: "/primes-cee", label: "Primes CEE" },
  { to: "/fournisseurs-partenaires", label: "Fournisseurs partenaires" },
  { to: "/veille-marche", label: "Veille Marché" },
  { to: "/centre-de-ressources", label: "Centre de ressources" },
  { to: "/faq", label: "FAQ" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="font-display font-semibold mb-4 text-sm tracking-[0.18em] uppercase text-primary-foreground/70">{title}</h4>
      <ul className="space-y-2 text-primary-foreground/85 text-sm">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="hover:text-accent transition-colors">{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Trois pages existent en deux versions. Le pied de page doit renvoyer vers
   celle de l'univers où se trouve le visiteur, comme le fait déjà le menu. */
const PART_ALIASES = {
  "/fournisseurs-partenaires": "/particuliers/fournisseurs-partenaires",
  "/veille-marche": "/particuliers/veille-marche",
};
const PRO_ALIASES = {
  "/primes-cee": "/professionnels/primes-cee",
};

export default function Footer() {
  const c = useSiteContent(keysFor("footer"));
  const { pathname } = useLocation();
  const audience = pathname.startsWith("/particuliers")
    ? "particulier"
    : pathname.startsWith("/professionnels")
      ? "pro"
      : (typeof document !== "undefined" ? document.documentElement.getAttribute("data-audience") : null);
  const aliases = audience === "particulier" ? PART_ALIASES : audience === "pro" ? PRO_ALIASES : {};
  const colSite = COL_SITE.map((l) => (aliases[l.to] ? { ...l, to: aliases[l.to] } : l));
  const email = c("footer.v2.email", "contact@energy-market.fr");
  const linkedin = c("footer.v2.linkedin", "https://www.linkedin.com");
  const logo = c("footer.v2.logo", "/img/logo-energy-market-blanc.png");
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="em-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start lg:items-center">
          {/* LOGO GÉANT */}
          <div className="lg:col-span-3 flex items-center justify-center lg:justify-start">
            <img src={logo} alt="Energy Market" className="h-20 md:h-28 lg:h-36 w-auto" />
          </div>

          {/* 4 SECTIONS */}
          <div className="lg:col-span-9">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 lg:gap-16">
              <FooterCol title="Professionnels" links={COL_PRO} />
              <FooterCol title="Particuliers" links={COL_PART} />
              <FooterCol title="Le site" links={colSite} />
              <div>
                <h4 className="font-display font-semibold mb-4 text-sm tracking-[0.18em] uppercase text-primary-foreground/70">Contact</h4>
                <ul className="space-y-3 text-primary-foreground/85 text-sm">
                  <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /><span>{c("footer.v2.location", "France entière")}</span></li>
                  <li className="flex items-center gap-2"><Phone className="h-4 w-4" /><span className="text-primary-foreground/60">{c("footer.v2.phone", "Téléphone à venir")}</span></li>
                  <li className="flex items-center gap-2"><Mail className="h-4 w-4" /><a href={`mailto:${email}`} className="hover:text-accent">{email}</a></li>
                  <li className="pt-2"><a href={linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:text-accent transition-colors" aria-label="LinkedIn"><Linkedin className="h-5 w-5" /></a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* TEXTE EN DESSOUS DE TOUT */}
        <div className="mt-12 pt-8 border-t border-white/10 flex justify-between items-start">
          <p className="text-primary-foreground/80 leading-relaxed text-sm md:text-base max-w-none lg:whitespace-nowrap">
            {c("footer.v2.note", "Votre courtier en énergie pour les particuliers et les professionnels. Comprendre, comparer, choisir — un accompagnement clair et pédagogique partout en France.")}
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="em-container py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-primary-foreground/70">
          <p>© {new Date().getFullYear()} {c("footer.v2.copyright", "Energy Market — Tous droits réservés.")}</p>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/mentions-legales" className="hover:text-accent">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" className="hover:text-accent">Politique de confidentialité</Link>
            <Link to="/gestion-des-cookies" className="hover:text-accent">Gestion des cookies</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
